/* eslint-disable react/jsx-boolean-value */
import * as uuid from "uuid";
import Dialog from "@material-ui/core/Dialog";
import DialogContent from "@material-ui/core/DialogContent";
import Typography from "@material-ui/core/Typography";
import React, { ChangeEvent, FC, useState } from "react";
import { AppBar, Tab, Tabs } from "@material-ui/core";
import { useDispatch, useSelector } from "react-redux";

import CreateNewAdGroupTab from "../../../../../views/CampaignCreationBasicSlides/AdGroupSelectionSlide/tabs/CreateNewAdGroupTab";
import Loader from "../../../../../components/elements/Loader";
import MediaTypeSelector from "../../../../../views/CampaignCreationBasicSlides/AdGroupSelectionSlide/components/MediaTypeSelector/MediaTypeSelector";
import SelectSavedAdGroupTab from "../../../../../views/CampaignCreationBasicSlides/AdGroupSelectionSlide/tabs/SelectSavedAdGroupTab";
import api from "../../../../../api";
import classes from "./UploadCreativesModal.module.scss";
import {
  AdGroupTemplate,
  AdGroupTemplateCreationBody,
  AdGroupType,
  CreativeAsset,
} from "../../../../../types/adGroupTypes";
import { AdGroupToCreateType } from "../../../../../types/campaignTypes";
import { MediaType } from "../../../../../views/CampaignCreationBasicSlides/AdGroupSelectionSlide/components/MediaTypeSelector/types";
import { ReduxApplicationState } from "../../../../../types/ReduxTypes/ReduxApplicationState";
import { a11yProps } from "../../../../../utils/a11yProps";
import {
  createAdGroup,
  createAdGroupTemplate,
  createCreativeTemplates,
  setAdGroupTemplate,
  setAdGroupToCreate,
  setCampaignBasicDetails,
  uploadCreativeAssets,
} from "../../../../../redux/reducers/campaignToCreate";
import { errorToaster, successToaster } from "../../../../../helpers/toasters";

type UploadCreativeModalProps = {
  isOpen: boolean;
  handleClose: () => void;
  beeswaxAdvertiserId: number;
  brandgenAdvertiserId: string;
  landingPage: string;
  fetchedAdGroupId: string;
  fetchAndSetAdGroup: () => Promise<void>;
  brandgenCampaignId: string;
  fetchedCreativesAssets: CreativeAsset[];
};

const UploadCreativesModal: FC<UploadCreativeModalProps> = ({
  isOpen,
  handleClose,
  beeswaxAdvertiserId,
  brandgenAdvertiserId,
  landingPage,
  fetchedAdGroupId,
  fetchAndSetAdGroup,
  brandgenCampaignId,
  fetchedCreativesAssets,
}) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isDisabled, setIsDisabled] = useState<boolean>(false);
  const [mediaType, setMediaType] = useState<MediaType>("display");
  const campaignToCreate = useSelector((state: ReduxApplicationState) => state.campaignToCreate);
  const { basicDetails } = campaignToCreate!;
  const dispatch = useDispatch();

  const [adGroupSelectionTab, setAdGroupSelectionTab] = useState<0 | 1>(0);

  const user = useSelector((state: ReduxApplicationState) => state.user);

  const handleTabChangeChange = (_: ChangeEvent<{}>, newTabValue: 0 | 1) => {
    setAdGroupSelectionTab(newTabValue);
    dispatch(setAdGroupTemplate(newTabValue === 0));
  };

  const handleMediaTypeChange = (selectedMediaType: MediaType) => {
    setMediaType(selectedMediaType);
    dispatch(setCampaignBasicDetails({ ...basicDetails, isVideoCampaign: selectedMediaType === "video" }));
  };

  const updateAdGroup = async (values: AdGroupToCreateType, createTemplate: boolean = true) => {
    const { name, description = "", creativeAssets } = values;

    if (creativeAssets.length === 0 || isDisabled) {
      errorToaster("Please, upload creative assets to continue.", 5000);
      return;
    }

    try {
      setIsLoading(true);
      setIsDisabled(true);

      const adGroupType = creativeAssets[0].fileType as AdGroupType;

      const adGroupBody = {
        adGroupId: `bg-ad-group-${uuid.v4()}`,
        brandgenAdvertiserId: brandgenAdvertiserId!,
        brandgenCampaignId,
        createdBy: user.currentUser,
        name,
        adGroupType,
        description,
      };

      const createdAdGroup = await createAdGroup(adGroupBody);
      if (!createdAdGroup) throw new Error("Failed to create AdGroup.");

      let createdAdGroupTemplate: AdGroupTemplate | undefined;
      if (createTemplate) {
        const adGroupTemplateBody: AdGroupTemplateCreationBody = {
          id: `bg-ad-group-template-${uuid.v4()}`,
          brandgenAdvertiserId: createdAdGroup.brandgenAdvertiserId!,
          createdBy: createdAdGroup.createdBy,
          name: createdAdGroup.name,
          adGroupType: createdAdGroup.adGroupType,
          description: createdAdGroup.description,
        };

        createdAdGroupTemplate = await createAdGroupTemplate(adGroupTemplateBody);
      }

      const uploadedCreatives = await uploadCreativeAssets(
        creativeAssets,
        beeswaxAdvertiserId.toString(),
        brandgenAdvertiserId,
        landingPage,
        createdAdGroup.adGroupId,
        api.changeCreative,
        brandgenCampaignId,
      );

      if (createTemplate && createdAdGroupTemplate) {
        await createCreativeTemplates(uploadedCreatives, createdAdGroupTemplate.id);
      }

      await api.deleteAdGroup(fetchedAdGroupId);

      await Promise.all(
        fetchedCreativesAssets.map((fetchedCreativeAsset) =>
          api.deleteCreative({
            bgCreativeId: fetchedCreativeAsset.bgCreativeId,
            beeswaxCreativeId: fetchedCreativeAsset.beeswaxCreativeId,
          }),
        ),
      );

      await fetchAndSetAdGroup();

      dispatch(
        setAdGroupToCreate({
          name: "",
          description: "",
          adGroupType: AdGroupType.IMAGE,
          creativeAssets: [],
        }),
      );

      successToaster("Creatives successfully replaced.", 2000);
    } catch (error) {
      console.error("Error updating AdGroup:", error);
      errorToaster("There was an error while upload new Ad Group", 5000);
    } finally {
      setIsLoading(false);
      setIsDisabled(false);
      handleClose();
    }
  };

  return (
    <Dialog
      open={isOpen}
      onClose={isLoading ? () => null : handleClose}
      aria-labelledby="Segment Creation Modal"
      className={classes.container}
      maxWidth="lg"
    >
      <div className={classes.dialogTitle}>
        <Typography variant="h2">Upload new AdGroup</Typography>
      </div>
      {isLoading ? (
        <div className={classes.loader}>
          <Loader isAbsolutePosition={false} size={36} />
        </div>
      ) : (
        <DialogContent className={classes.dialogContent}>
          <div
            style={{
              margin: "3rem 0",
              width: "50rem",
              display: "flex",
              gap: 50,
            }}
          >
            <MediaTypeSelector mediaType={mediaType} onChange={handleMediaTypeChange} disableVideo={true} />
          </div>
          <AppBar position="static" className={classes.tabs}>
            <Tabs value={adGroupSelectionTab} onChange={handleTabChangeChange} aria-label="AdGroup Selection Tabs">
              <Tab label="Select From Saved AdGroups" {...a11yProps(0)} />
              <Tab label="Create New AdGroup" {...a11yProps(1)} />
            </Tabs>
          </AppBar>

          <CreateNewAdGroupTab selectedTabPanelValue={adGroupSelectionTab} updateAdGroup={updateAdGroup} />

          <SelectSavedAdGroupTab
            selectedTabPanelValue={adGroupSelectionTab}
            updateAdGroup={updateAdGroup}
            showSubmitButton
          />
        </DialogContent>
      )}
    </Dialog>
  );
};

export default UploadCreativesModal;
